import React from "react";
import { FaMapMarkerAlt, FaSignal } from "react-icons/fa";
import { MdWarning } from "react-icons/md";
import {
  MapContainer,
  TileLayer,
  Circle,
  LayersControl,
  LayerGroup,
  Popup,
  Marker,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// İkon resimlerini import et
const iconImages = {
  cayfiltremakinesi: require("../../../Assets/icons/cayfiltremakinesi.png"),
  dortludegirmenli: require("../../../Assets/icons/dortludegirmenli.png"),
  caykahvebirarada: require("../../../Assets/icons/caykahvebirarada.png"),
  default: "https://cdn-icons-png.flaticon.com/512/1995/1995470.png",
};

const getDeviceIcon = (deviceName) => {
  let iconUrl = iconImages.default;

  if (deviceName) {
    const deviceNameLower = deviceName.toLowerCase();
    if (deviceNameLower.includes("çayset")) {
      if (deviceNameLower.includes("fincanda")) {
        iconUrl = iconImages.caykahvebirarada;
      } else {
        iconUrl = iconImages.cayfiltremakinesi;
      }
    } else if (deviceNameLower.includes("fincanda")) {
      iconUrl = iconImages.dortludegirmenli;
    }
  }

  return new L.Icon({
    iconUrl: iconUrl,
    iconSize: [60, 60],
    iconAnchor: [30, 30],
    popupAnchor: [0, -30],
  });
};

function MapTab({ selectedDevice }) {
  if (!selectedDevice) return null;

  // Konum bilgisi yoksa uyarı mesajı göster
  if (!selectedDevice.locationInfo) {
    return (
      <div className="p-4">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md flex items-start">
          <MdWarning className="text-yellow-400 w-6 h-6 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-yellow-800 font-medium text-lg mb-1">
              Konum Bilgisi Bulunamadı
            </h3>
            <p className="text-yellow-700">
              Bu cihaz için konum bilgisi henüz kaydedilmemiş veya cihaz konum
              bilgisi paylaşmıyor.
            </p>
            <div className="mt-4 flex items-center gap-2 text-yellow-600">
              <FaMapMarkerAlt className="w-5 h-5" />
              <span className="font-medium">
                Cihaz:{" "}
                {selectedDevice.deviceInfo?.deviceName ||
                  selectedDevice.serialNo}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const locationData = selectedDevice.locationInfo || {
    coordinates: {
      latitude: 41.0082,
      longitude: 28.9784,
    },
    accuracy: 100,
    altitude: 0,
    updatedInfo: new Date(),
  };

  const connectionData = selectedDevice.deviceInfo?.connection || {
    status: false,
    lastConnected: new Date(),
  };

  const position = {
    lat: locationData.coordinates.latitude,
    lng: locationData.coordinates.longitude,
  };

  const accuracyRadius = locationData.accuracy || 100;
  const deviceIcon = getDeviceIcon(selectedDevice.deviceInfo?.deviceName);

  const formatDate = (date) => {
    if (!date) return "Bilinmiyor";
    const d = new Date(date);
    return `${d.toLocaleDateString("tr-TR")} ${d.toLocaleTimeString("tr-TR")}`;
  };

  const getConnectionStatus = () => {
    if (connectionData.status) {
      return {
        color: "green",
        text: "Bağlı",
        bgColor: "bg-green-100",
        textColor: "text-green-700",
      };
    } else {
      return {
        color: "red",
        text: "Bağlı Değil",
        bgColor: "bg-red-100",
        textColor: "text-red-700",
      };
    }
  };

  const connectionStatus = getConnectionStatus();

  return (
    <div className="flex flex-col h-full -m-2 sm:-m-4">
      {/* Konum Bilgi Barı */}
      <div className="shrink-0 bg-gray-50/80 border-b border-gray-100 px-3 sm:px-5 py-2.5 sm:py-3">
        {/* Mobil: tek satırda sarmalanan etiketler */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] sm:text-xs">
          <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 font-semibold px-2 py-1 rounded-md">
            <FaMapMarkerAlt className="w-3 h-3" />
            {position.lat.toFixed(4)}, {position.lng.toFixed(4)}
          </span>
          <span className="text-gray-400">Doğruluk: <span className="font-semibold text-gray-700">{accuracyRadius.toLocaleString("tr-TR")} m</span></span>
          <span className="text-gray-400">Yükseklik: <span className="font-semibold text-gray-700">{locationData.altitude?.toLocaleString("tr-TR") ?? 0} m</span></span>
          <span className={`inline-flex items-center gap-1 font-bold ${connectionData.status ? "text-emerald-600" : "text-red-600"}`}>
            <FaSignal className="w-3 h-3" />
            {connectionStatus.text}
          </span>
          <span className="text-gray-400 sm:ml-auto">Güncelleme: <span className="font-medium text-gray-600">{formatDate(locationData.updatedInfo)}</span></span>
        </div>
      </div>

      {/* Harita - Tüm alana yayılır */}
      <div className="flex-1 relative min-h-0">
        <MapContainer
          center={[position.lat, position.lng]}
          zoom={14}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
          className="[&_.leaflet-control-container]:!z-[400]"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <LayersControl position="topright">
            <LayersControl.Overlay checked name="Cihaz Konumu">
              <LayerGroup>
                <Circle
                  center={[position.lat, position.lng]}
                  pathOptions={{
                    color: connectionStatus.color,
                    fillColor: connectionStatus.color,
                    fillOpacity: 0.2,
                  }}
                  radius={accuracyRadius}
                />

                <Marker
                  position={[position.lat, position.lng]}
                  icon={deviceIcon}
                >
                  <Popup>
                    <div className="text-center p-1">
                      <div
                        className={`${connectionStatus.bgColor} ${connectionStatus.textColor} px-2 py-1 rounded-full text-xs font-medium inline-block mb-2`}
                      >
                        {connectionStatus.text}
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 text-xs">
                        <div className="text-left font-medium">Enlem:</div>
                        <div className="text-left">{position.lat.toFixed(4)}</div>
                        <div className="text-left font-medium">Boylam:</div>
                        <div className="text-left">{position.lng.toFixed(4)}</div>
                        <div className="text-left font-medium">Doğruluk:</div>
                        <div className="text-left">{accuracyRadius.toLocaleString("tr-TR")} m</div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              </LayerGroup>
            </LayersControl.Overlay>
          </LayersControl>
        </MapContainer>
      </div>
    </div>
  );
}

export default MapTab;
